import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, inputCls } from '../../utils/operations';

const AUTOMATIONS = [
  ['email_requests', 'Email ao Admin por novo pedido'],
  ['email_applications', 'Email ao Admin por candidatura'],
  ['email_proposals', 'Email de proposta ao cliente'],
  ['email_vouchers', 'Email de voucher ao cliente'],
  ['whatsapp_notifications', 'Notificações WhatsApp automatizadas'],
  ['review_requests', 'Pedido de review após viagem'],
  ['auto_voucher_on_payment', 'Voucher automático após pagamento confirmado'],
  ['auto_payout_on_voucher', 'Payout automático no ledger após voucher'],
  ['ai_assistance', 'Assistente AI de conteúdo'],
];

export const AdminSettings = ({ settings, onRefresh }) => {
  const [rates, setRates] = useState({});
  const [form, setForm] = useState(null);
  const [newCurrency, setNewCurrency] = useState({ code: '', rate: '' });
  const [fxStatus, setFxStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch('/fx/rates')
      .then((doc) => setFxStatus(doc))
      .catch(() => setFxStatus({ error: true }));
  }, []);

  useEffect(() => {
    if (!settings) return;
    setRates(settings.rates || {});
    setForm({
      rates_note: settings.rates_note || '',
      default_margin_pct: settings.default_margin_pct ?? 20,
      payout_due_days: settings.payout_due_days ?? 7,
      public_price_approval_required: settings.public_price_approval_required !== false,
      whatsapp_number: settings.whatsapp_number || '',
      automations: settings.automations || {},
      stay22: {
        enabled: settings.integrations?.stay22?.enabled !== false,
        embed_id: settings.integrations?.stay22?.embed_id || '',
        default_campaign: settings.integrations?.stay22?.default_campaign || 'eco_accommodations',
        lma_id: settings.integrations?.stay22?.lma_id || '',
      },
      financial_accounts: settings.financial_accounts || [],
    });
  }, [settings]);

  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try {
      await apiFetch('/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rates,
          rates_note: form.rates_note,
          default_margin_pct: Number(form.default_margin_pct),
          payout_due_days: Number(form.payout_due_days),
          public_price_approval_required: form.public_price_approval_required,
          whatsapp_number: form.whatsapp_number,
          automations: form.automations,
          integrations: { stay22: { enabled: form.stay22.enabled, embed_id: form.stay22.embed_id, default_campaign: form.stay22.default_campaign } },
          financial_accounts: form.financial_accounts,
        }),
      });
      toast.success('Configuração guardada');
      await onRefresh();
    } catch (err) {
      toast.error('Configuração não guardada', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };

  const addCurrency = () => {
    const code = newCurrency.code.trim().toUpperCase();
    const rate = Number(newCurrency.rate);
    if (!/^[A-Z]{3}$/.test(code) || !(rate > 0)) return toast.error('Use um código de 3 letras e uma taxa positiva');
    setRates((r) => ({ ...r, [code]: rate }));
    setNewCurrency({ code: '', rate: '' });
  };

  const integrations = settings.integrations || {};
  return (
    <div className="space-y-6" data-testid="admin-settings-panel">
      <section className="rounded-xl border border-eco-ink/10 bg-white p-6">
        <h2 className="font-serif text-xl font-semibold text-eco-ink">Moeda, margens e payouts</h2>
        <p className="mt-1 text-sm text-eco-ink/55">Base EUR. As taxas usadas ficam congeladas em cada proposta/pagamento/payout.</p>
        <p data-testid="fx-provider-status" className="mt-2 rounded-lg bg-eco-cream px-3 py-2 text-xs text-eco-ink/60">
          {fxStatus === null && 'A verificar taxas públicas (open.er-api.com)…'}
          {fxStatus?.error && 'Taxas automáticas indisponíveis — o site usa as taxas manuais abaixo ou mostra o preço original em EUR.'}
          {fxStatus && !fxStatus.error && (
            <>
              Seletor público de moeda: {fxStatus.source === 'manual' ? 'a usar taxas manuais (fallback)' : 'taxas automáticas open.er-api.com'}
              {fxStatus.stale ? ' (última versão conhecida)' : ''} · atualizadas em {fxStatus.last_update ? new Date(fxStatus.last_update).toLocaleString('pt-PT') : '—'}.
              Se o fornecedor falhar, as taxas manuais completas (USD, GBP, CVE, STN) servem de fallback.
            </>
          )}
        </p>
        <div className="mt-5 grid gap-4 md:grid-cols-4">
          {Object.entries(rates).map(([code, rate]) => (
            <label key={code}>
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">{code}</span>
              <input data-testid={`rate-${code}`} type="number" min="0.000001" step="0.000001" value={rate} disabled={code === 'EUR'} onChange={(e) => setRates({ ...rates, [code]: Number(e.target.value) })} className={`${inputCls} mt-1 disabled:bg-eco-cream`} />
            </label>
          ))}
          <div className="flex items-end gap-2">
            <input data-testid="new-currency-code" value={newCurrency.code} onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })} placeholder="CHF" className={inputCls} maxLength={3} />
            <input data-testid="new-currency-rate" type="number" step="0.000001" value={newCurrency.rate} onChange={(e) => setNewCurrency({ ...newCurrency, rate: e.target.value })} placeholder="Taxa" className={inputCls} />
            <button data-testid="add-currency" onClick={addCurrency} className="inline-flex min-h-11 items-center rounded-lg bg-eco-green px-4 text-sm font-semibold text-white transition-colors hover:bg-eco-deep">Adicionar</button>
          </div>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-4">
          <label><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Margem default %</span><input data-testid="default-margin" type="number" min="0" max="80" step="0.1" value={form.default_margin_pct} onChange={(e) => setForm({ ...form, default_margin_pct: e.target.value })} className={`${inputCls} mt-1`} /></label>
          <label><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Payout due days</span><input data-testid="payout-due-days" type="number" min="0" max="90" value={form.payout_due_days} onChange={(e) => setForm({ ...form, payout_due_days: e.target.value })} className={`${inputCls} mt-1`} /></label>
          <label className="md:col-span-2"><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Nota das taxas</span><input data-testid="rates-note" value={form.rates_note} onChange={(e) => setForm({ ...form, rates_note: e.target.value })} className={`${inputCls} mt-1`} /></label>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-eco-ink/70">
          <input data-testid="price-approval-required" type="checkbox" checked={form.public_price_approval_required} onChange={(e) => setForm({ ...form, public_price_approval_required: e.target.checked })} className="h-4 w-4 accent-eco-green" />
          Exigir aprovação Admin antes de mostrar preço público
        </label>
      </section>

      <section className="rounded-xl border border-eco-ink/10 bg-white p-6">
        <h2 className="font-serif text-xl font-semibold text-eco-ink">Automações</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {AUTOMATIONS.map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-4 rounded-lg bg-eco-cream px-4 py-3 text-sm font-medium text-eco-ink/70">
              {label}
              <input data-testid={`automation-${key}`} type="checkbox" checked={Boolean(form.automations[key])} onChange={(e) => setForm({ ...form, automations: { ...form.automations, [key]: e.target.checked } })} className="h-4 w-4 accent-eco-green" />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-eco-ink/10 bg-white p-6" data-testid="settings-financial-accounts">
        <h2 className="font-serif text-xl font-semibold text-eco-ink">Contas financeiras</h2>
        <p className="mt-1 text-sm text-eco-ink/55">Contas reais da Eco Collection. As contas marcadas "visível ao cliente" aparecem nas instruções de pagamento da proposta aceite — preenche o IBAN para isso.</p>
        <div className="mt-5 space-y-4">
          {form.financial_accounts.map((acc, i) => (
            <div key={acc.id} data-testid={`finance-account-${acc.id}`} className="rounded-xl border border-eco-ink/10 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-eco-ink">{acc.name}</p>
                <span className="rounded-full bg-eco-ink/5 px-2.5 py-0.5 text-[10px] font-bold text-eco-ink/50">{acc.currency}</span>
                <span className="rounded-full bg-eco-cream px-2.5 py-0.5 text-[10px] font-semibold text-eco-ink/55">{acc.purpose}</span>
                <span className="ml-auto flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-eco-ink/70"><input type="checkbox" data-testid={`finance-account-public-${acc.id}`} checked={acc.show_to_customer} onChange={(e) => setForm({ ...form, financial_accounts: form.financial_accounts.map((a, j) => j === i ? { ...a, show_to_customer: e.target.checked } : a) })} className="h-3.5 w-3.5 accent-eco-green" />Visível ao cliente</label>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-eco-ink/70"><input type="checkbox" data-testid={`finance-account-active-${acc.id}`} checked={acc.active} onChange={(e) => setForm({ ...form, financial_accounts: form.financial_accounts.map((a, j) => j === i ? { ...a, active: e.target.checked } : a) })} className="h-3.5 w-3.5 accent-eco-green" />Ativa</label>
                </span>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">IBAN / Número de conta</span><input data-testid={`finance-account-iban-${acc.id}`} value={acc.iban} onChange={(e) => setForm({ ...form, financial_accounts: form.financial_accounts.map((a, j) => j === i ? { ...a, iban: e.target.value } : a) })} placeholder="IBAN ou dados da conta" className={`${inputCls} mt-1`} /></label>
                <label><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Detalhes (BIC/SWIFT, titular…)</span><input data-testid={`finance-account-details-${acc.id}`} value={acc.details} onChange={(e) => setForm({ ...form, financial_accounts: form.financial_accounts.map((a, j) => j === i ? { ...a, details: e.target.value } : a) })} placeholder="BIC, titular, notas" className={`${inputCls} mt-1`} /></label>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-eco-ink/10 bg-white p-6">
        <h2 className="font-serif text-xl font-semibold text-eco-ink">Integrações</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {[
            ['Email transacional', integrations.email_configured, integrations.owner_email_configured ? 'Admin email configurado' : 'OWNER_EMAIL em falta'],
            ['AI assistida', integrations.llm_configured, 'Gemini via Emergent'],
            ['Object storage', integrations.storage_configured, 'Upload otimizado WebP'],
            ['WhatsApp automático', integrations.whatsapp_service_configured, integrations.whatsapp_service_configured ? 'Serviço configurado' : 'Serviço WhatsApp não configurado'],
          ].map(([label, ok, detail]) => (
            <div key={label} data-testid={`integration-${label.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-lg bg-eco-cream px-4 py-3">
              <p className="text-sm font-semibold text-eco-ink">{label}</p>
              <p className={`mt-1 text-xs ${ok ? 'text-eco-green' : 'text-amber-700'}`}>{ok ? 'Configurado' : 'Não configurado'} · {detail}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-eco-ink/10 bg-eco-cream p-4" data-testid="integration-stay22">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-eco-ink">Stay22 — alojamento externo</p>
              <p className="mt-1 text-xs text-eco-ink/55">
                lmaID: {settings.integrations?.stay22?.lma_id_masked || 'não configurado'} · Script letmeallez.js carregado no site · Estado: {form.stay22.enabled ? <span className="font-semibold text-eco-green">Ativo</span> : <span className="font-semibold text-amber-700">Desligado</span>}
              </p>
            </div>
            <label className="flex items-center gap-2.5 text-sm font-medium text-eco-ink">
              <input data-testid="stay22-enabled-toggle" type="checkbox" checked={form.stay22.enabled} onChange={(e) => setForm({ ...form, stay22: { ...form.stay22, enabled: e.target.checked } })} className="h-4 w-4 accent-eco-green" />
              Stay22 ativo
            </label>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Embed ID (widget do mapa)</span><input data-testid="stay22-embed-id" value={form.stay22.embed_id} onChange={(e) => setForm({ ...form, stay22: { ...form.stay22, embed_id: e.target.value } })} className={`${inputCls} mt-1`} /></label>
            <label><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Campanha default (tracking)</span><input data-testid="stay22-campaign" value={form.stay22.default_campaign} onChange={(e) => setForm({ ...form, stay22: { ...form.stay22, default_campaign: e.target.value } })} className={`${inputCls} mt-1`} /></label>
          </div>
          <p className="mt-2 text-xs text-eco-ink/45">Alojamento Stay22 é inventário externo — nunca é marcado como parceiro Eco Collection. Configuração por destino em Destinations → Alojamento.</p>
        </div>
        <label className="mt-5 block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">WhatsApp Business oficial (formato internacional)</span>
          <input data-testid="settings-whatsapp-number" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className={`${inputCls} mt-1`} placeholder="Ex: 239…" />
        </label>
        <p className="mt-2 text-xs text-eco-ink/45">O envio automático fica registado como não configurado até existir um serviço WhatsApp Business real. Nenhum número fictício é usado.</p>
      </section>

      <button data-testid="settings-save" onClick={save} disabled={busy} className="eco-btn-primary disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar configuração
      </button>
    </div>
  );
};
